import { createLogger } from "./logger";

const logger = createLogger("DeviceFingerprint");

export interface DeviceInfo {
  userAgent: string;
  language: string;
  platform: string;
  screenResolution: string;
  timezone: string;
  colorDepth: number;
  hardwareConcurrency: number;
  deviceMemory?: number;
  cookieEnabled: boolean;
  doNotTrack: string | null;
}

/**
 * Generates a device fingerprint for security purposes
 * This is used to bind refresh tokens to specific devices
 */
export async function generateDeviceFingerprint(): Promise<string> {
  if (typeof window === "undefined") {
    // Server-side fallback
    return "server-side-fallback";
  }

  try {
    const deviceInfo: DeviceInfo = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenResolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      colorDepth: screen.colorDepth,
      hardwareConcurrency: navigator.hardwareConcurrency,
      deviceMemory: (navigator as any).deviceMemory,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
    };

    // Create a stable string from device characteristics
    const fingerprintData = [
      deviceInfo.userAgent,
      deviceInfo.language,
      deviceInfo.platform,
      deviceInfo.screenResolution,
      deviceInfo.timezone,
      deviceInfo.colorDepth.toString(),
      deviceInfo.hardwareConcurrency.toString(),
      deviceInfo.deviceMemory?.toString() || "unknown",
      deviceInfo.cookieEnabled.toString(),
      deviceInfo.doNotTrack || "null",
    ].join("|");

    // Use Web Crypto API for hashing if available
    if (window.crypto && window.crypto.subtle) {
      const encoder = new TextEncoder();
      const data = encoder.encode(fingerprintData);
      const hashBuffer = await window.crypto.subtle.digest("SHA-256", data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
    }

    // Fallback to a simple hash if Web Crypto is not available
    return btoa(fingerprintData)
      .replace(/[^a-zA-Z0-9]/g, "")
      .substring(0, 64);
  } catch (error) {
    logger.warn("Failed to generate device fingerprint", { error });
    // Return a fallback fingerprint based on basic info
    const fallback = `${navigator.userAgent}-${screen.width}x${screen.height}`;
    return btoa(fallback)
      .replace(/[^a-zA-Z0-9]/g, "")
      .substring(0, 64);
  }
}

/**
 * Get device info for debugging/logging purposes
 */
export function getDeviceInfo(): Partial<DeviceInfo> {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    return {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenResolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      colorDepth: screen.colorDepth,
      hardwareConcurrency: navigator.hardwareConcurrency,
      deviceMemory: (navigator as any).deviceMemory,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
    };
  } catch (error) {
    logger.warn("Failed to get device info", { error });
    return {};
  }
}
